"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import type { QuickAddOption } from "./workspace-quick-add.helpers";

type QuickAddSelectionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  options: QuickAddOption[];
  confirmLabel?: string;
  onConfirm: (selectedIds: string[]) => Promise<void>;
};

export function QuickAddSelectionModal({
  open,
  onOpenChange,
  title,
  description,
  options,
  confirmLabel = "Add selected",
  onConfirm,
}: QuickAddSelectionModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectableOptions = useMemo(
    () => options.filter((option) => !option.disabled),
    [options]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(selectableOptions.map((option) => option.id)));
  }, [open, selectableOptions]);

  const toggleOption = (optionId: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(optionId);
      } else {
        next.delete(optionId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedIds.size === 0) return;
    setIsSubmitting(true);
    try {
      await onConfirm([...selectedIds]);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {options.map((option) => {
              const inputId = `quick-add-${option.id}`;
              const isChecked = selectedIds.has(option.id);

              return (
                <div
                  key={option.id}
                  className="flex items-start gap-3 rounded-lg border p-3"
                >
                  <Checkbox
                    id={inputId}
                    checked={isChecked}
                    disabled={option.disabled || isSubmitting}
                    onCheckedChange={(checked) =>
                      toggleOption(option.id, checked === true)
                    }
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor={inputId}
                      className={
                        option.disabled
                          ? "text-muted-foreground"
                          : "cursor-pointer font-medium"
                      }
                    >
                      {option.label}
                    </Label>
                    {option.description ? (
                      <p className="text-xs text-muted-foreground">
                        {option.description}
                      </p>
                    ) : null}
                    {option.disabled && option.disabledReason ? (
                      <p className="text-xs text-muted-foreground">
                        {option.disabledReason}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isSubmitting || selectedIds.size === 0}
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
